import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { MEDICATION_RESOURCE_TYPE } from "../resource-configs/medication-columns";

type ResourceTypeConfig = {
  resourceType: string;
  displayName: string;
  getterMethod: keyof FhirBundleSdk | "special"; // "special" for resources that need custom handling
  observationCategory?: string; // For filtering observation types
};

// Configuration for supported FHIR resource types in specified tab order
export const RESOURCE_TYPES: ResourceTypeConfig[] = [
  { resourceType: "Suspects", displayName: "Suspects", getterMethod: "special" },
  {
    resourceType: "DiagnosticReport",
    displayName: "Notes",
    getterMethod: "getDiagnosticReports",
  },
  {
    resourceType: "ImagingReports",
    displayName: "Imaging",
    getterMethod: "getDiagnosticReports",
  },
  { resourceType: "Condition", displayName: "Conditions", getterMethod: "getConditions" },
  {
    resourceType: MEDICATION_RESOURCE_TYPE,
    displayName: "Medications",
    getterMethod: "getMedications",
  },
  {
    resourceType: "AllergyIntolerance",
    displayName: "Allergies",
    getterMethod: "getAllergyIntolerances",
  },
  { resourceType: "Procedure", displayName: "Procedures", getterMethod: "getProcedures" },
  {
    resourceType: "LaboratoryObservations",
    displayName: "Labs",
    getterMethod: "getObservations",
    observationCategory: "laboratory",
  },
  {
    resourceType: "VitalSignObservations",
    displayName: "Vitals",
    getterMethod: "getObservations",
    observationCategory: "vital-signs",
  },
  {
    resourceType: "SurveysObservations",
    displayName: "Surveys",
    getterMethod: "getObservations",
    observationCategory: "survey",
  },
  {
    resourceType: "SocialHistoryObservations",
    displayName: "Social History",
    getterMethod: "getObservations",
    observationCategory: "social-history",
  },
  { resourceType: "Immunization", displayName: "Immunizations", getterMethod: "getImmunizations" },
  { resourceType: "CarePlan", displayName: "Care Plans", getterMethod: "special" },
  {
    resourceType: "FamilyMemberHistory",
    displayName: "Family Member History",
    getterMethod: "getFamilyMemberHistories",
  },
  {
    resourceType: "RelatedPerson",
    displayName: "Related Persons",
    getterMethod: "getRelatedPersons",
  },
  { resourceType: "Coverage", displayName: "Coverage", getterMethod: "getCoverages" },
  { resourceType: "Encounter", displayName: "Encounters", getterMethod: "getEncounters" },
  {
    resourceType: "DocumentReference",
    displayName: "Raw Documents",
    getterMethod: "getDocumentReferences",
  },
];

/**
 * Extracts numeric or string values from FHIR resources for chart/graph display.
 * Returns undefined for timeline-only resources (no numeric values).
 */
export function getValueForResourceType(resource: unknown): number | string | undefined {
  if (!resource || typeof resource !== "object") return undefined;

  const resourceObj = resource as Record<string, unknown>;

  // For Observation resources
  if (resourceObj.resourceType === "Observation") {
    const obs = resourceObj as { valueQuantity?: { value?: number }; valueString?: string };
    return obs.valueQuantity?.value ?? obs.valueString;
  }

  // For Immunization resources - no numeric values, just dates (timeline mode)
  if (resourceObj.resourceType === "Immunization") {
    return undefined; // This will trigger timeline mode
  }

  // For Medication resources - no numeric values, just dates (timeline mode)
  if (resourceObj.resourceType === "Medication") {
    return undefined; // This will trigger timeline mode
  }

  // For MedicationWithRefs objects (from MedicationSeries)
  if (resourceObj.medication && resourceObj.administration && resourceObj.dispense) {
    // This is a MedicationWithRefs object - timeline mode for medication events
    return undefined;
  }

  // For EnhancedMedicationSeries objects (new medication series structure)
  if (resourceObj.events && Array.isArray(resourceObj.events) && resourceObj.medicationWithRefs) {
    // This is an EnhancedMedicationSeries object - timeline mode for medication events
    return undefined;
  }

  // For Condition resources - no numeric values, just dates (timeline mode)
  if (resourceObj.resourceType === "Condition") {
    return undefined; // This will trigger timeline mode
  }

  // For other resource types, return undefined to show timeline mode
  return undefined;
}

/**
 * Extracts the relevant date from a FHIR resource based on its type.
 * Different resource types store dates in different fields.
 * Covers all resource types supported by the FHIR Bundle SDK.
 */
export function getDateForResourceType(resource: unknown): string | undefined {
  if (!resource || typeof resource !== "object") return undefined;

  const resourceObj = resource as Record<string, unknown>;

  // For EnhancedMedicationSeries objects (new medication series structure)
  if (resourceObj.events && Array.isArray(resourceObj.events) && resourceObj.latestDate) {
    const enhancedSeries = resourceObj as { latestDate: string };
    return enhancedSeries.latestDate;
  }

  // For MedicationWithRefs objects (from MedicationSeries) - legacy support
  if (resourceObj.medication && resourceObj.administration && resourceObj.dispense) {
    // This is a MedicationWithRefs object - use the same logic as getMedicationDate from series-utils
    const medWithRefs = resourceObj as {
      administration: Array<{
        effectiveDateTime?: string;
        effectivePeriod?: { start?: string; end?: string };
      }>;
      statement: Array<{
        effectiveDateTime?: string;
        effectivePeriod?: { start?: string; end?: string };
      }>;
      dispense: Array<{ whenHandedOver?: string }>;
      requests: Array<{ authoredOn?: string }>;
    };

    const adminDates = medWithRefs.administration
      .map(a => a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end)
      .filter(Boolean);

    const statementDates = medWithRefs.statement
      .map(s => s.effectiveDateTime || s.effectivePeriod?.start || s.effectivePeriod?.end)
      .filter(Boolean);

    const dispenseDates = medWithRefs.dispense.map(d => d.whenHandedOver).filter(Boolean);
    const requestDates = medWithRefs.requests.map(r => r.authoredOn).filter(Boolean);
    const allDates = [...adminDates, ...statementDates, ...dispenseDates, ...requestDates];

    if (allDates.length === 0) return undefined;

    return allDates.sort(
      (a, b) => new Date(b as string).getTime() - new Date(a as string).getTime()
    )[0] as string;
  }

  // For Patient resources
  if (resourceObj.resourceType === "Patient") {
    const patient = resourceObj as { birthDate?: string; deceasedDateTime?: string };
    return patient.deceasedDateTime || patient.birthDate || undefined;
  }

  // For Observation resources
  if (resourceObj.resourceType === "Observation") {
    const obs = resourceObj as {
      effectiveDateTime?: string;
      effectivePeriod?: { start?: string; end?: string };
      issued?: string;
    };
    return (
      obs.effectiveDateTime ||
      obs.effectivePeriod?.start ||
      obs.effectivePeriod?.end ||
      obs.issued ||
      undefined
    );
  }

  // For Encounter resources
  if (resourceObj.resourceType === "Encounter") {
    const encounter = resourceObj as { period?: { start?: string; end?: string } };
    return encounter.period?.start || encounter.period?.end || undefined;
  }

  // For AllergyIntolerance resources
  if (resourceObj.resourceType === "AllergyIntolerance") {
    const allergy = resourceObj as {
      reaction?: Array<{
        substance?: { text?: string; coding?: Array<{ display?: string }> };
      }>;
      code?: { text?: string; coding?: Array<{ display?: string }> };
    };

    // Try to get allergy name from reaction substance first, then from code
    const allergyName =
      allergy.reaction?.[0]?.substance?.text ||
      allergy.reaction?.[0]?.substance?.coding?.[0]?.display ||
      allergy.code?.text ||
      allergy.code?.coding?.[0]?.display ||
      "Unknown allergy";

    return allergyName;
  }

  // For Condition resources
  if (resourceObj.resourceType === "Condition") {
    const condition = resourceObj as {
      onsetDateTime?: string;
      onsetPeriod?: { start?: string; end?: string };
      recordedDate?: string;
      abatementDateTime?: string;
    };
    return (
      condition.onsetDateTime ||
      condition.onsetPeriod?.start ||
      condition.onsetPeriod?.end ||
      condition.recordedDate ||
      condition.abatementDateTime ||
      undefined
    );
  }

  // For Immunization resources
  if (resourceObj.resourceType === "Immunization") {
    const imm = resourceObj as { occurrenceDateTime?: string; recorded?: string };
    return imm.occurrenceDateTime || imm.recorded || undefined;
  }

  // For Procedure resources
  if (resourceObj.resourceType === "Procedure") {
    const procedure = resourceObj as {
      performedDateTime?: string;
      performedPeriod?: { start?: string; end?: string };
    };
    return (
      procedure.performedDateTime ||
      procedure.performedPeriod?.start ||
      procedure.performedPeriod?.end ||
      undefined
    );
  }

  // For DiagnosticReport resources
  if (resourceObj.resourceType === "DiagnosticReport") {
    const report = resourceObj as {
      effectiveDateTime?: string;
      effectivePeriod?: { start?: string; end?: string };
      issued?: string;
    };
    return (
      report.effectiveDateTime ||
      report.effectivePeriod?.start ||
      report.effectivePeriod?.end ||
      report.issued ||
      undefined
    );
  }

  // For DocumentReference resources
  if (resourceObj.resourceType === "DocumentReference") {
    const docRef = resourceObj as {
      date?: string;
      context?: { period?: { start?: string; end?: string } };
    };
    return docRef.date || docRef.context?.period?.start || docRef.context?.period?.end || undefined;
  }

  // For Medication resources
  if (resourceObj.resourceType === "Medication") {
    const med = resourceObj as {
      effectiveDateTime?: string;
      effectivePeriod?: { start?: string; end?: string };
    };
    return (
      med.effectiveDateTime || med.effectivePeriod?.start || med.effectivePeriod?.end || undefined
    );
  }

  // For MedicationAdministration resources
  if (resourceObj.resourceType === "MedicationAdministration") {
    const medAdmin = resourceObj as {
      effectiveDateTime?: string;
      effectivePeriod?: { start?: string; end?: string };
    };
    return (
      medAdmin.effectiveDateTime ||
      medAdmin.effectivePeriod?.start ||
      medAdmin.effectivePeriod?.end ||
      undefined
    );
  }

  // For MedicationDispense resources
  if (resourceObj.resourceType === "MedicationDispense") {
    const medDispense = resourceObj as {
      whenHandedOver?: string;
      whenPrepared?: string;
    };
    return medDispense.whenHandedOver || medDispense.whenPrepared || undefined;
  }

  // For MedicationRequest resources
  if (resourceObj.resourceType === "MedicationRequest") {
    const medRequest = resourceObj as { authoredOn?: string };
    return medRequest.authoredOn || undefined;
  }

  // For MedicationStatement resources
  if (resourceObj.resourceType === "MedicationStatement") {
    const medStatement = resourceObj as {
      effectiveDateTime?: string;
      effectivePeriod?: { start?: string; end?: string };
      dateAsserted?: string;
    };
    return (
      medStatement.effectiveDateTime ||
      medStatement.effectivePeriod?.start ||
      medStatement.effectivePeriod?.end ||
      medStatement.dateAsserted ||
      undefined
    );
  }

  // For ServiceRequest resources
  if (resourceObj.resourceType === "ServiceRequest") {
    const serviceReq = resourceObj as {
      occurrenceDateTime?: string;
      occurrencePeriod?: { start?: string; end?: string };
      authoredOn?: string;
    };
    return (
      serviceReq.occurrenceDateTime ||
      serviceReq.occurrencePeriod?.start ||
      serviceReq.occurrencePeriod?.end ||
      serviceReq.authoredOn ||
      undefined
    );
  }

  // For FamilyMemberHistory resources
  if (resourceObj.resourceType === "FamilyMemberHistory") {
    const familyHistory = resourceObj as { date?: string };
    return familyHistory.date || undefined;
  }

  // For Coverage resources
  if (resourceObj.resourceType === "Coverage") {
    const coverage = resourceObj as { period?: { start?: string; end?: string } };
    return coverage.period?.start || coverage.period?.end || undefined;
  }

  // For Composition resources
  if (resourceObj.resourceType === "Composition") {
    const composition = resourceObj as { date?: string };
    return composition.date || undefined;
  }

  // For RiskAssessment resources
  if (resourceObj.resourceType === "RiskAssessment") {
    const riskAssessment = resourceObj as {
      occurrenceDateTime?: string;
      occurrencePeriod?: { start?: string; end?: string };
    };
    return (
      riskAssessment.occurrenceDateTime ||
      riskAssessment.occurrencePeriod?.start ||
      riskAssessment.occurrencePeriod?.end ||
      undefined
    );
  }

  // For Organization, Location, Practitioner, RelatedPerson resources
  // These typically don't have clinically relevant dates for ordering
  if (
    resourceObj.resourceType === "Organization" ||
    resourceObj.resourceType === "Location" ||
    resourceObj.resourceType === "Practitioner" ||
    resourceObj.resourceType === "RelatedPerson"
  ) {
    return undefined;
  }

  return undefined;
}

/**
 * Extracts a human-readable display value from a FHIR resource.
 * Formats the display differently based on resource type and includes relevant metadata.
 */
export function getDisplayValueForResourceType(resource: unknown): string {
  if (!resource || typeof resource !== "object") return "-";

  const resourceObj = resource as Record<string, unknown>;

  // For EnhancedMedicationSeries objects (new medication series structure)
  if (resourceObj.events && Array.isArray(resourceObj.events) && resourceObj.medicationWithRefs) {
    const enhancedSeries = resourceObj as {
      codeDisplay: string;
      events: Array<{ type: string }>;
    };

    const medName = enhancedSeries.codeDisplay;

    // Get event counts by type for display
    const eventCounts = enhancedSeries.events.reduce((counts, event) => {
      counts[event.type] = (counts[event.type] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);

    const eventDescriptions = [];
    if (eventCounts.request && eventCounts.request > 0)
      eventDescriptions.push(`${eventCounts.request} prescribed`);
    if (eventCounts.dispense && eventCounts.dispense > 0)
      eventDescriptions.push(`${eventCounts.dispense} dispensed`);
    if (eventCounts.administration && eventCounts.administration > 0)
      eventDescriptions.push(`${eventCounts.administration} administered`);
    if (eventCounts.statement && eventCounts.statement > 0)
      eventDescriptions.push(`${eventCounts.statement} statements`);

    return `${medName} (${eventDescriptions.join(", ") || "No events"})`;
  }

  // For MedicationWithRefs objects (from MedicationSeries) - legacy support
  if (resourceObj.medication && resourceObj.administration && resourceObj.dispense) {
    const medWithRefs = resourceObj as {
      medication: {
        code?: { text?: string; coding?: Array<{ display?: string }> };
      };
      administration: Array<{ status?: string }>;
      dispense: Array<{ status?: string }>;
      statement: Array<{ status?: string }>;
      requests: Array<{ status?: string }>;
    };

    const medName =
      medWithRefs.medication.code?.text ||
      medWithRefs.medication.code?.coding?.[0]?.display ||
      "Unknown medication";

    // Get event counts for display
    const adminCount = medWithRefs.administration.length;
    const dispenseCount = medWithRefs.dispense.length;
    const statementCount = medWithRefs.statement.length;
    const requestCount = medWithRefs.requests.length;

    const eventCounts = [];
    if (requestCount > 0) eventCounts.push(`${requestCount} prescribed`);
    if (dispenseCount > 0) eventCounts.push(`${dispenseCount} dispensed`);
    if (adminCount > 0) eventCounts.push(`${adminCount} administered`);
    if (statementCount > 0) eventCounts.push(`${statementCount} statements`);

    return `${medName} (${eventCounts.join(", ") || "No events"})`;
  }

  // For Observation resources
  if (resourceObj.resourceType === "Observation") {
    const obs = resourceObj as {
      valueQuantity?: { value?: number; unit?: string };
      valueString?: string;
      valueCodeableConcept?: { text?: string; coding?: Array<{ display?: string }> };
    };

    if (obs.valueQuantity) {
      const unit = obs.valueQuantity.unit?.replace(/[{()}]/g, "") || "";
      return `${obs.valueQuantity.value || ""} ${unit}`.trim();
    } else if (obs.valueString) {
      return obs.valueString;
    } else if (obs.valueCodeableConcept) {
      return obs.valueCodeableConcept.text || obs.valueCodeableConcept.coding?.[0]?.display || "-";
    }
  }

  // For Immunization resources
  if (resourceObj.resourceType === "Immunization") {
    const imm = resourceObj as {
      vaccineCode?: { text?: string; coding?: Array<{ display?: string }> };
      status?: string;
    };
    const vaccineName =
      imm.vaccineCode?.text || imm.vaccineCode?.coding?.[0]?.display || "Unknown vaccine";
    const status = imm.status || "Unknown status";
    return `${vaccineName} (${status})`;
  }

  // For Condition resources
  if (resourceObj.resourceType === "Condition") {
    const condition = resourceObj as {
      code?: { text?: string; coding?: Array<{ display?: string }> };
      clinicalStatus?: { coding?: Array<{ code?: string }> };
      verificationStatus?: { coding?: Array<{ code?: string }> };
    };

    const conditionName =
      condition.code?.text || condition.code?.coding?.[0]?.display || "Unknown condition";
    const clinicalStatus = condition.clinicalStatus?.coding?.[0]?.code || "unknown";
    const verificationStatus = condition.verificationStatus?.coding?.[0]?.code || "unknown";

    return `${conditionName} (${clinicalStatus}, ${verificationStatus})`;
  }

  // For Medication-related resources - display dosage
  if (resourceObj.resourceType === "MedicationAdministration") {
    const admin = resourceObj as {
      dosage?: { dose?: { value?: number; unit?: string } };
    };
    const dose = admin.dosage?.dose;
    if (dose?.value && dose?.unit) {
      return `${dose.value} ${dose.unit}`;
    }
    return "-";
  }

  if (resourceObj.resourceType === "MedicationDispense") {
    const dispense = resourceObj as {
      dosageInstruction?: Array<{
        doseAndRate?: Array<{ doseQuantity?: { value?: number; unit?: string } }>;
      }>;
    };
    const doseQuantity = dispense.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity;
    if (doseQuantity?.value && doseQuantity?.unit) {
      return `${doseQuantity.value} ${doseQuantity.unit}`;
    }
    return "-";
  }

  if (resourceObj.resourceType === "MedicationRequest") {
    const request = resourceObj as {
      dosageInstruction?: Array<{
        doseAndRate?: Array<{ doseQuantity?: { value?: number; unit?: string } }>;
      }>;
    };
    const doseQuantity = request.dosageInstruction?.[0]?.doseAndRate?.[0]?.doseQuantity;
    if (doseQuantity?.value && doseQuantity?.unit) {
      return `${doseQuantity.value} ${doseQuantity.unit}`;
    }
    return "-";
  }

  if (resourceObj.resourceType === "MedicationStatement") {
    const statement = resourceObj as {
      dosage?: Array<{
        doseAndRate?: Array<{ doseQuantity?: { value?: number; unit?: string } }>;
      }>;
    };
    const doseQuantity = statement.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity;
    if (doseQuantity?.value && doseQuantity?.unit) {
      return `${doseQuantity.value} ${doseQuantity.unit}`;
    }
    return "-";
  }

  if (resourceObj.resourceType === "Medication") {
    // Medication resource itself doesn't have dosage info
    return "-";
  }

  return "-";
}

/**
 * Returns the appropriate Y-axis label for charting a given resource type.
 */
export function getYAxisLabelForResourceType(resourceType: string): string {
  switch (resourceType) {
    case "Observation":
      return "Value";
    case "Immunization":
      return "Count";
    case "Medication":
      return "Dose";
    default:
      return "Value";
  }
}

/**
 * Gets the human-readable display name for a FHIR resource type.
 */
export function getResourceDisplayName(resourceType: string): string {
  const config = RESOURCE_TYPES.find(r => r.resourceType === resourceType);
  return config?.displayName || resourceType;
}
